import {observer} from 'mobx-react';
import '../geography-filter.scss';
import {ReactNode, useContext, useEffect, useMemo, useRef} from 'react';
import {NetworkContext} from '../../../lib/context';
import {Props as NOProps} from '../no-filter';
import {runInAction} from 'mobx';
import {ReportsPageTab, ViewType} from '../../../pages/reports-page/useReportsPageController';
import {StandardType} from '../../../lib/types';
import FilterContainer from '../filter-container';

interface IReportsNOFiltersProps {
    selectedViewType: ViewType;
    activeTab: ReportsPageTab;
}

const ReportsNOFilters = observer((props: IReportsNOFiltersProps) => {
    
    const {selectedViewType, activeTab} = props;

    const noStore = useContext(NetworkContext);
    const hasReset = useRef(false);
    // Reset only once on initial mount using ref to prevent flicker
    // using useRef to track if we've already reset, and use runInAction from MobX to batch the updates
    if (!hasReset.current) {
        runInAction(() => {
            noStore.appliedGeographyFilter.state = '';
            noStore.appliedGeographyFilter.county = '';
        });
        hasReset.current = true;
    }
    
    useEffect(() => {
        if (noStore.runId && noStore.availableGeographyFilters.length === 0 && !noStore.loadingGeographyData) {
            noStore.loadGeographyData();
        }
    }, [noStore.runId, noStore.readyToLoadData()]);

    useEffect(() => {
        const controller = new AbortController();
        const loadSpecialtiesData = async () => {
            // Clear specialty filter when switching roster view types (Provider/Facility/Combined)
            // because specialties from one type are not valid for another type
            noStore.setSpecialtyFilter('');
            await noStore.loadSpecialtiesData(controller, selectedViewType.name, false);
        };
        loadSpecialtiesData();
        return () => controller.abort();
    }, [
        noStore.runId,
        noStore.readyToLoadData(),
        selectedViewType.name
    ]);

    const adequacyStandardFilter: NOProps = useMemo(() => {
        const data = noStore.getGeoFilterUniqueValuesByKey('GEOGRAPHY', 'standard_type');
        const onChange = (e: string) => {
            noStore.setGeographyFilter('standard_type', e);
        };
        const children: [(item: string) => string, (item: string) => ReactNode] = [
            (item: string) => item,
            (item: string) => item,
        ];
        return {
            label: 'Adequacy Standard',
            data,
            value: noStore.appliedGeographyFilter.standard_type,
            required: true,
            onChange,
            children,
            spinner: noStore.loadingGeographyData,
            ['data-testid']: 'roster-adequacy-standard-dropdown',
        };
    }, [noStore.runId, noStore.loadingGeographyData, noStore.appliedGeographyFilter.standard_type]);

    const standardVersionFilter: NOProps = useMemo(() => {
        const geoFilterArgs = {
            standard_type: noStore.appliedGeographyFilter.standard_type,
        };
        const data = noStore.getGeoFilterUniqueValuesByKey(
            'GEOGRAPHY',
            'standard_version',
            geoFilterArgs,
            '',
            'DESC',
        );
        const onChange = (e: string) => {
            noStore.setGeographyFilter('standard_version', e);
        };
        const children: [(item: any, i: number) => string, (item: any, i: number) => ReactNode] = [
            (item: any): string => item,
            (item: any): ReactNode => item,
        ];
        return {
            label: 'Standard Version',
            data,
            value: noStore.appliedGeographyFilter.standard_version,
            required: true,
            onChange,
            children,
            spinner: noStore.loadingGeographyData,
            ['data-testid']: 'standard-version-dropdown',
        };
    }, [
        noStore.runId,
        noStore.loadingGeographyData,
        adequacyStandardFilter,
        noStore.appliedGeographyFilter.standard_version
    ]);

    const patientRosterFilter: NOProps = useMemo(() => {
        const geoFilterArgs = {
            standard_type: noStore.appliedGeographyFilter.standard_type,
            standard_version: noStore.appliedGeographyFilter.standard_version,
        };
        const data = noStore.getGeoFilterUniqueValuesByKey(
            'GEOGRAPHY',
            'patient_roster_name',
            geoFilterArgs,
        );
        const onChange = (e: string) => {
            noStore.setGeographyFilter('patient_roster_name', e);
        };
        const children: [(item: any, i: number) => string, (item: any, i: number) => ReactNode] = [
            (item: any): string => item,
            (item: any): ReactNode => item,
        ];
        return {
            label: 'Patient Roster',
            data,
            value: noStore.appliedGeographyFilter.patient_roster_name,
            required: true,
            onChange,
            children,
            spinner: noStore.loadingGeographyData,
            ['data-testid']: 'patient-roster-dropdown',
        };
    }, [
        noStore.runId,
        noStore.loadingGeographyData,
        adequacyStandardFilter,
        standardVersionFilter,
        noStore.appliedGeographyFilter.patient_roster_name
    ]);
    
    const stateFilter: NOProps = useMemo(() => {
        const geoFilterArgs = {
            patient_roster_name: noStore.appliedGeographyFilter.patient_roster_name,
            standard_type: noStore.appliedGeographyFilter.standard_type,
            standard_version: noStore.appliedGeographyFilter.standard_version,
        };
        const data = noStore.getGeoFilterUniqueValuesByKey(
            'GEOGRAPHY',
            'state_additional_info.state',
            activeTab === ReportsPageTab.NetworkAdequacy ? geoFilterArgs : undefined
        );
        const onChange = (value: string) => {
            noStore.setGeographyFilter('state', value);
        };
        const children: [(item: string) => string, (item: string) => ReactNode] = [
            (item: string) => item,
            (item: string) => item,
        ];
        return {
            label: 'State',
            data,
            value: noStore.appliedGeographyFilter.state,
            onChange,
            children,
            spinner: noStore.loadingGeographyData,
            enableMultiSelect: true,
            disabled: adequacyStandardFilter.value === StandardType.QHP,
            ['data-testid']: 'roster-state-dropdown',
        };
    }, [
        activeTab,
        noStore.runId,
        noStore.loadingGeographyData,
        noStore.appliedGeographyFilter.state,
        adequacyStandardFilter,
        standardVersionFilter
    ]);
    
    const countyFilter: NOProps = useMemo(() => {
        const geoFilterArgs = {
            'state_additional_info.state': noStore.appliedGeographyFilter.state,
            standard_type: noStore.appliedGeographyFilter.standard_type,
            ...(activeTab === ReportsPageTab.NetworkAdequacy ? {
                patient_roster_name: noStore.appliedGeographyFilter.patient_roster_name,
                standard_version: noStore.appliedGeographyFilter.standard_version,
            } : {})
        };
        const data = noStore.getGeoFilterUniqueValuesByKey('GEOGRAPHY', 
            ['county_additional_info.county_name', 'ssacd'],
            geoFilterArgs
        );        
        const onChange = (value: string) => {
            noStore.setGeographyFilter('county', value);
        };
        const children: [(item: string) => string, (item: string) => ReactNode] = [
            (item: string) => item,
            (item: string) => item
        ];
        const disabled = adequacyStandardFilter.value === StandardType.QHP || !noStore.appliedGeographyFilter.state;
        return {
            label: 'County',
            data,
            value: noStore.appliedGeographyFilter.county,
            onChange,
            children,
            spinner: noStore.loadingGeographyData,
            disabled,
            enableMultiSelect: true,
            ['data-testid']: 'roster-county-dropdown'
        };
    }, [
        noStore.runId,
        noStore.loadingGeographyData,
        noStore.appliedGeographyFilter.county,
        adequacyStandardFilter,
        stateFilter,
        standardVersionFilter,
        patientRosterFilter,
        activeTab
    ]);

    const specialtyFilter: NOProps = useMemo(() => {
        const source =
            noStore.defaultComplianceScores.length > 0
                ? noStore.defaultComplianceScores
                : noStore.complianceScores;
        const data = source.map((score) => score.name);
        const onChange = (value: string) => {
            noStore.setSpecialtyFilter(value);
        };
        const children: [(item: any) => string, (item: any) => ReactNode] = [
            (item: any) => item,
            (item: any) => item,
        ];
        const disabled = !noStore.appliedGeographyFilter.standard_type;
        return {
            label: 'Specialty',
            data,
            value: noStore.specialtyFilter,
            onChange,
            children,
            spinner: noStore.loadingSpecialtyData,
            enableMultiSelect: true,
            disabled,
            ['data-testid']: 'roster-specialty-dropdown',
        };
    }, [
        noStore.runId,
        noStore.complianceScores,
        noStore.appliedGeographyFilter.standard_type,
        noStore.loadingSpecialtyData,
        noStore.specialtyFilter,
    ]);

    useEffect(() => {
        if (!noStore.appliedGeographyFilter.standard_type) {
            noStore.setSpecialtyFilter('');
        }
    }, [noStore.appliedGeographyFilter.standard_type]);

    const filterConfiguration: Array<NOProps> = useMemo(() => {
        if (activeTab === ReportsPageTab.Roster) {
            return [
                adequacyStandardFilter,
                stateFilter,
                countyFilter,
                specialtyFilter
            ];
        } else if (activeTab === ReportsPageTab.NetworkAdequacy) {
            return [
                adequacyStandardFilter,
                standardVersionFilter,
                patientRosterFilter,
                stateFilter,
                countyFilter,
                specialtyFilter
            ];
        } else {
            return [];
        }
    }, [
        activeTab,
        adequacyStandardFilter,
        standardVersionFilter,
        patientRosterFilter,
        stateFilter,
        countyFilter,
        specialtyFilter
    ]);

    return (
        <FilterContainer
            filterConfiguration={filterConfiguration}
            horizontal={true}
        />
    );
});

export default ReportsNOFilters;